import React from 'react';
import { VisualDataTypeWithAttributes } from 'Variables/VariablesTypes';
import { DatePickerInputStub } from 'components/DatePicker';
import { MonetaryInputStub } from 'components/MonetaryInput';
import { PercentageInputStub } from 'components/PercentageInput';
import { getCurrencySymbol } from 'components/CurrencySelect/currencies';
import { PhoneNumberInputStub } from 'components/PhoneNumberInput';
import { IdentificationNumberInputStub } from 'components/IdentificationNumberInput';
import { TextInputStub } from 'components/TextInput';
import { isDateDataType, isIdentificationNumber, isMonetaryDataType, isPhoneNumber } from './InputWithDataType';
import { AutoCompletionStub } from 'components/AutoCompletion';
import { InputTooltipType } from 'components/TextInput/TextInputStub';

export interface InputWithDataTypeStubProps {
  label: string;
  disabled?: boolean;
  className?: string;
  elementBeforeInput?: JSX.Element | null;
  inputIcon?: React.ReactNode;
  markerIcons?: React.ReactNode;
  required?: boolean;
  inputTooltip?: React.ReactNode;
  inputTooltipType?: InputTooltipType;
}

const InputWithDataTypeStub = (props: InputWithDataTypeStubProps & VisualDataTypeWithAttributes) => {
  const { dataType, inputIcon, markerIcons, disabled, className, elementBeforeInput, label, inputTooltip, inputTooltipType, required } = props;

  if (isPhoneNumber(props)) {
    const { phoneNumberFormat } = props;

    return (
      <PhoneNumberInputStub
        label={label}
        elementBeforeInput={elementBeforeInput}
        disabled={disabled}
        className={className}
        country={phoneNumberFormat}
        inputIcon={inputIcon}
        markerIcons={markerIcons}
        required={required}
        inputTooltip={inputTooltip}
        inputTooltipType={inputTooltipType}
      />
    );
  }

  if (isIdentificationNumber(props)) {
    const { identificationNumberType, identificationNumberDescription } = props;

    return (
      <IdentificationNumberInputStub
        label={label}
        className={className}
        disabled={disabled}
        identificationNumberType={identificationNumberType!}
        identificationNumberDescription={identificationNumberDescription}
        elementBeforeInput={elementBeforeInput}
        inputIcon={inputIcon}
        markerIcons={markerIcons}
        required={required}
        inputTooltip={inputTooltip}
        inputTooltipType={inputTooltipType}
      />
    );
  }

  if (isMonetaryDataType(props)) {
    const { currency } = props;
    const currencySymbol = getCurrencySymbol(currency!);

    return (
      <MonetaryInputStub
        label={label}
        disabled={disabled}
        className={className}
        placeholder={dataType}
        currencySymbol={currencySymbol}
        elementBeforeInput={elementBeforeInput}
        inputIcon={inputIcon}
        markerIcons={markerIcons}
        required={required}
        inputTooltip={inputTooltip}
        inputTooltipType={inputTooltipType}
      />
    );
  }

  if (isDateDataType(props)) {
    const { dateFormat } = props;

    return (
      <DatePickerInputStub
        label={label}
        disabled={disabled}
        className={className}
        dateFormat={dateFormat!}
        elementBeforeInput={elementBeforeInput}
        inputIcon={inputIcon}
        markerIcons={markerIcons}
        required={required}
        inputTooltip={inputTooltip}
        inputTooltipType={inputTooltipType}
      />
    );
  }
  switch (dataType) {
    case 'Percentage': {
      return (
        <PercentageInputStub
          label={label}
          disabled={disabled}
          className={className}
          placeholder={dataType}
          elementBeforeInput={elementBeforeInput}
          inputIcon={inputIcon}
          markerIcons={markerIcons}
          required={required}
          inputTooltip={inputTooltip}
          inputTooltipType={inputTooltipType}
        />
      );
    }
    case 'Number': {
      return (
        <TextInputStub
          label={label}
          disabled={disabled}
          className={className}
          placeholder={dataType}
          elementBeforeInput={elementBeforeInput}
          inputIcon={inputIcon}
          markerIcons={markerIcons}
          required={required}
          inputTooltip={inputTooltip}
          inputTooltipType={inputTooltipType}
        />
      );
    }
    case 'Boolean':
    case 'List': {
      return (
        <AutoCompletionStub
          label={label}
          disabled={disabled}
          className={className}
          placeholder={dataType}
          elementBeforeInput={elementBeforeInput}
          inputIcon={inputIcon}
          markerIcons={markerIcons}
          required={required}
          inputTooltip={inputTooltip}
          inputTooltipType={inputTooltipType}
        />
      );
    }
    default:
      return (
        <TextInputStub
          label={label}
          disabled={disabled}
          className={className}
          placeholder={dataType === 'EmailAddress' ? 'email@domain.com' : dataType}
          elementBeforeInput={elementBeforeInput}
          inputIcon={inputIcon}
          markerIcons={markerIcons}
          required={required}
          inputTooltip={inputTooltip}
          inputTooltipType={inputTooltipType}
        />
      );
  }
};

export default InputWithDataTypeStub;
